import {
    Component,
    OnInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    AnimationCallbackEvent,
    input,
    signal,
    inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { DatePickerModule } from 'primeng/datepicker';
import { BadgeModule } from 'primeng/badge';

import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TranslatePipe } from '@ngx-translate/core';
import { gsap } from 'gsap';

import moment from 'moment';
import { Api } from '../../../../../services/api.service';
import {
    IDispatchCampaign,
    IDispatchCampaignTargetFilterCondition
} from '../../../../../../../../../../common/common-interfaces/admin/crm/dispatch-campaign';
import { IClient } from '../../../../../../../../../../common/common-interfaces/client';
import { IPackage } from '../../../../../../../../../../common/common-interfaces/package';
import { debugLogger } from '@common-helpers/debug-logger';

@Component({
    selector: 'dispatch-campaign-target-filter-editor',
    templateUrl: './target-filter-editor.component.html',
    styleUrls: ['./target-filter-editor.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        CardModule,
        ButtonModule,
        SelectModule,
        SkeletonModule,
        DatePickerModule,
        ToggleSwitchModule,
        TranslatePipe,
        BadgeModule
    ]
})
export class DispatchCampaignTargetFilterEditorComponent implements OnInit {
    private cdr = inject(ChangeDetectorRef);
    private api = inject(Api);

    dispatchCampaign = input.required<IDispatchCampaign>();
    filterMode = input<string>('');

    clients = signal<IClient[]>([]);
    clientsLoading = signal<boolean>(true);
    availablePackagesByClient: { [clientId: string]: IPackage[] } = {};

    async ngOnInit(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();

        if (!dispatchCampaign.targetFilter) {
            dispatchCampaign.targetFilter = {
                conditions: []
            };
        }

        if (!dispatchCampaign.targetFilter.conditions.length) {
            await this.addCondition();
        }

        this.clientsLoading.set(true);
        const clientsResult = await this.api.currentorganization().get<IClient[]>(`/clients/available`);
        this.clients.set(clientsResult.data ?? []);
        this.clientsLoading.set(false);

        for (const condition of dispatchCampaign.targetFilter.conditions) {
            if (!condition.clientId) {
                const clients = this.clients();
                condition.clientId = clients.length ? clients[0].id : null;
                await this.clientIdChanged(condition);
            }

            if (condition.clientId) {
                await this.secureClientIdDataForCondition(condition);
            }
        }
        this.cdr.markForCheck();
    }

    async addCondition(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        const condition: IDispatchCampaignTargetFilterCondition = {
            action: 'INCLUDE',
            type: 'ALL_CUSTOMERS',
            data: {}
        };
        await this.secureClientIdDataForCondition(condition);
        dispatchCampaign.targetFilter.conditions.push(condition);
        this.cdr.markForCheck();
    }

    async conditionTypeChanged(condition: IDispatchCampaignTargetFilterCondition): Promise<void> {
        debugLogger.info('Condition type changed:', condition.type);
        this.cdr.markForCheck();
    }

    async clientIdChanged(condition: IDispatchCampaignTargetFilterCondition): Promise<void> {
        debugLogger.info('Client ID changed:', condition.clientId);

        await this.secureClientIdDataForCondition(condition);
        if (condition.data.SPECIFIC_BOOKINGS) {
            condition.data.SPECIFIC_BOOKINGS.packageIds = [];
        }
        this.cdr.markForCheck();
    }

    async secureClientIdDataForCondition(condition: IDispatchCampaignTargetFilterCondition): Promise<void> {
        if (condition.clientId && !this.availablePackagesByClient[condition.clientId]) {
            const packages = await this.api.client(condition.clientId).get<IPackage[]>(`/packages`);
            this.availablePackagesByClient[condition.clientId] = packages.data ?? [];
        }

        if (!condition.data.SPECIFIC_BOOKINGS) {
            condition.data.SPECIFIC_BOOKINGS = {
                bookingDate: {
                    enabled: false,
                    start: null,
                    end: null
                },
                visitDate: {
                    enabled: true,
                    start: moment().subtract(6, 'months').toDate(),
                    end: moment().toDate()
                },
                packageIds: []
            };
        }

        if (!condition.data.ALL_CUSTOMERS) {
            condition.data.ALL_CUSTOMERS = {
                newsletterAcceptedOnly: true
            };
        }

        this.cdr.markForCheck();
    }

    async moveConditionUp(condition: IDispatchCampaignTargetFilterCondition): Promise<void> {
        const conditions = this.dispatchCampaign().targetFilter.conditions;
        const index = conditions.indexOf(condition);
        if (index > 0) {
            conditions.splice(index, 1);
            conditions.splice(index - 1, 0, condition);
        }
        this.cdr.markForCheck();
    }

    async moveConditionDown(condition: IDispatchCampaignTargetFilterCondition): Promise<void> {
        const conditions = this.dispatchCampaign().targetFilter.conditions;
        const index = conditions.indexOf(condition);
        if (index < conditions.length - 1) {
            conditions.splice(index, 1);
            conditions.splice(index + 1, 0, condition);
        }
        this.cdr.markForCheck();
    }

    async removeCondition(condition: IDispatchCampaignTargetFilterCondition): Promise<void> {
        const conditions = this.dispatchCampaign().targetFilter.conditions;
        const index = conditions.indexOf(condition);
        if (index > -1) {
            conditions.splice(index, 1);
        }
        this.cdr.markForCheck();
    }

    animateStepIn(event: AnimationCallbackEvent): void {
        const cards = event.target.querySelectorAll('p-card');

        if (cards.length) {
            gsap.fromTo(
                cards,
                {
                    opacity: 0,
                    y: 10
                },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.3,
                    ease: 'power2.inOut',
                    stagger: 0.1
                }
            );
        } else {
            gsap.fromTo(
                event.target.children,
                {
                    opacity: 0,
                    y: 10
                },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.3,
                    ease: 'power1.inOut',
                    stagger: 0.08
                }
            );
        }
    }
}
